import {
  ISettingsParam,
  createSettingsParams,
  SettingsParamType,
} from '@wix/tpa-settings';

import {
  IChallengeSettings,
  TextAlignment,
  ImageRatio,
  CropSelection,
  VisitorPageLayout,
  ImageAlignment,
  VerticalImageAlignment,
  SidebarPlacement,
  MobileSidebarPlacement,
} from './Settings/challengeSettings/challengeSettings.types';
import { RolesState } from './Widget/settingsEvents';
import { ImageShape } from '../ChallengesList/Settings/challengeSettings/challengeSettings.types';
import { SettingsKeysAbbreviation } from './Settings/constants/settingsKeysAbbreviation';
import challengeSettings from './settingsParams';

export default createSettingsParams<IChallengeSettings>({
  [SettingsKeysAbbreviation.visitorPageBodySectionsOrder]: {
    getDefaultValue() {
      return { order: [] };
    },
  },
  [SettingsKeysAbbreviation.visitorPageSidebarSectionsOrder]: {
    getDefaultValue() {
      return { order: [] };
    },
  },
  [SettingsKeysAbbreviation.visitorPageShareTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('live.challenges-page.social-share.title');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleShareTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageShareShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageOverviewShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageAboutShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageGroupDiscussionTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('live-site.groups.section-title');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleGroupDiscussionTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageGroupDiscussionShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageShowGroupImage]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPagePriceJoinButtonTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.visitor-page.enroll-button');
    },
  },
  [SettingsKeysAbbreviation.visitorPageTogglePriceJoinButtonTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPagePriceTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.pricing-options.title-for-free');
    },
  },
  [SettingsKeysAbbreviation.visitorPageTogglePriceTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPagePriceShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageStartDayJoinButtonTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.visitor-page.enroll-button');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleStartDayJoinButtonTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageStartDayTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.start-date-selection.title');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleStartDayTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageStartDayShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageInstructorShowInSidebar]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageInstructorTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.visitor-page.instructor-default-title');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleInstructorTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageAboutJoinButtonTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.visitor-page.enroll-button');
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleAboutJoinButtonTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  [SettingsKeysAbbreviation.visitorPageAboutTitle]: {
    type: SettingsParamType.Text,
    getDefaultValue({ t, getSettingParamValue }) {
      return (
        getSettingParamValue?.(challengeSettings.aboutVstText) ||
        t('settings.visitor-page.sections.about.title')
      );
    },
  },
  [SettingsKeysAbbreviation.visitorPageToggleAboutTitle]: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  [SettingsKeysAbbreviation.visitorPageAboutInstructorImageShape]: {
    getDefaultValue() {
      return ImageShape.Square;
    },
  },
  visitorPageBodySidebarTextAlignment: {
    getDefaultValue() {
      return TextAlignment.Left;
    },
  },
  visitorPageBodySidebarPlacement: {
    getDefaultValue() {
      return SidebarPlacement.right;
    },
  },
  visitorPageBodyMobileSidebarPlacement: {
    getDefaultValue() {
      return MobileSidebarPlacement.bottom;
    },
  },
  visitorPageBodyEnableSidebar: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  visitorPageImageAlignment: {
    getDefaultValue() {
      return ImageAlignment.left;
    },
  },
  visitorPageVerticalImageAlignment: {
    inheritDesktop: false,
    getDefaultValue({ isMobile }) {
      return isMobile
        ? VerticalImageAlignment.top
        : VerticalImageAlignment.bottom;
    },
  },
  visitorPageLayout: {
    getDefaultValue() {
      return VisitorPageLayout.classic;
    },
    inheritDesktop: false,
  },
  displayRewards: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayRewardsForVisitor: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayGroup: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  /**
   * sidebar layout
   */
  sidebarLayoutTextAlignment: {
    getDefaultValue({ isRTL }) {
      return isRTL ? TextAlignment.Right : TextAlignment.Left;
    },
  },
  sidebarLayoutSpace: {
    type: SettingsParamType.Number,
    inheritDesktop: false,
    getDefaultValue() {
      return 0;
    },
  },
  /**
   * participant page sidebar layout
   */
  sidebarLayoutDisplayChallengeDate: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  sidebarLayoutDisplayChallengeDuration: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  sidebarLayoutDisplayChallengeSteps: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  sidebarLayoutDisplayChallengeParticipants: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  /**
   * visitor settings
   */
  editorParticipantRole: {
    inheritDesktop: false,
    getDefaultValue() {
      return RolesState.Visitor;
    },
  },
  headerTextAlignment: {
    getDefaultValue({ isRTL }) {
      if (isRTL) {
        return TextAlignment.Right;
      }
      return TextAlignment.Left;
    },
  },
  contentTextAlignment: {
    getDefaultValue({ isRTL }) {
      return isRTL ? TextAlignment.Right : TextAlignment.Left;
    },
  },
  imageRatio: {
    getDefaultValue() {
      return ImageRatio.HD;
    },
  },
  cropSelection: {
    getDefaultValue() {
      return CropSelection.Middle;
    },
  },
  displayHeaderDate: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayHeaderPrice: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayHeaderImage: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayChallengeDuration: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayChallengeSteps: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return true;
    },
  },
  displayChallengeParticipants: {
    type: SettingsParamType.Boolean,
    getDefaultValue() {
      return false;
    },
  },
  displayOwner: {
    getDefaultValue() {
      return true;
    },
  },
  displayOneApp: {
    getDefaultValue() {
      return true;
    },
  },
  displayDivider: {
    getDefaultValue() {
      return true;
    },
  },
  displayPriceForFreePrograms: {
    getDefaultValue() {
      return true;
    },
  },
  socialSharingIsEnabled: {
    getDefaultValue() {
      return true;
    },
  },
  joinViaOneAppForParticipantIsEnabled: {
    getDefaultValue() {
      return false;
    },
  },
  textJoinButton: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.join');
    },
  },
  textRequestToJoinButton: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.request-to-join');
    },
  },

  /**
   * visitor settings: agenda
   */

  agendaIsEnabled: {
    getDefaultValue() {
      return false;
    },
  },
  agendaShowTitle: {
    getDefaultValue() {
      return true;
    },
  },
  agendaTitleText: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.tab.sections.agenda.section-title-default');
    },
  },
  agendaStepsShown: {
    getDefaultValue() {
      return 5;
    },
  },
  agendaShowMoreText: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('settings.tab.sections.agenda.load-all-steps-default');
    },
  },
  aboutVstText: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.content-title');
    },
  },
  completeButtonTxt: {
    type: SettingsParamType.Text,
    getDefaultValue({ t }) {
      return t('challenge.page.steps.complete');
    },
  },
  overviewTabDisplay: {
    getDefaultValue() {
      return true;
    },
  },
});

export const abbreviationToSettingKey = (
  key: SettingsKeysAbbreviation,
): ISettingsParam => {
  return challengeSettings[key];
};
